import Cookies from "js-cookie";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../Components/Loader/Loader";
import DynamicTable from "../../Components/Table/DynamicTable"; // Adjust path if needed
import { TablePagination } from "@mui/material";
import {
  EmpDayReport,
  fetchDailyTaskList,
  fetchDayReportByEmpId,
} from "../../Redux/Slice/Day";
import ConfirmModal from "../../Components/ConfirmModal/ConfirmModal";
import socket from "../../socket";

const DailyWork = () => {

  const dispatch = useDispatch();
  // const navigate = useNavigate();

  const loginData = Cookies.get("loginData");
  const parsedData = loginData ? JSON.parse(loginData) : null; // Use null if loginData is undefined

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";

    const d = new Date(dateStr);

    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const year = d.getUTCFullYear();

    const hours = String(d.getUTCHours()).padStart(2, "0");
    const minutes = String(d.getUTCMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  useEffect(() => {
    socket.on("newDayReport", (data) => {
      // Refresh own reports if the update is relevant
      setRefreshList(prev => prev + 1);
    });

    return () => {
      socket.off("newDayReport");
    };
  }, []);



  const { DailyTaskList, DayReportData } = useSelector((state) => state.Day);

  const [formValues, setFormValues] = useState({
    Description: "",
    memberid: parsedData?.data?._id,
    TaskId: null,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshList, setRefreshList] = useState(0);

  const allTaskOptions = useMemo(() => {
    const options = [];
    DailyTaskList?.data?.forEach((task) => {
      if (task?.CallNo && task.Status?.toLowerCase() !== "completed") {
        options.push({
          id: task?._id,
          callNo: task.CallNo,
          status: task.Status,
          presentStatus: task.PresentStatus,
        });
      }
      task?.RatingRecord?.filter((r) => r?.ConplainId && r.ConplainId.Status?.toLowerCase() !== "completed")?.forEach((r) => {
        options.push({
          id: r.ConplainId._id,
          callNo: r.ConplainId.CallNo,
          status: r.ConplainId.Status,
          presentStatus: r.ConplainId.PresentStatus,
        });
      });
    });
    return options;
  }, [DailyTaskList]);

  const selectedTaskDetails = useMemo(() => {
    if (!formValues.TaskId) return null;
    return allTaskOptions.find(t => String(t.id) === String(formValues.TaskId));
  }, [formValues.TaskId, allTaskOptions]);

  // const handleViewProfile = (taskId) => {
  //   navigate(`/task-profile/${taskId}`);
  // };

  useEffect(() => {
    dispatch(fetchDailyTaskList(parsedData?.data._id));
  }, [dispatch, parsedData?.data?._id]);

  useEffect(() => {
    if (parsedData?.data?._id) {
      dispatch(fetchDayReportByEmpId({
        memberId: parsedData.data._id,
        page: page + 1,
        limit: rowsPerPage
      }));
    }
  }, [dispatch, parsedData?.data?._id, page, rowsPerPage, refreshList]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "TaskId" && value) {
      const selectedTask = allTaskOptions.find(t => String(t.id) === String(value));
      const taskStatus = selectedTask?.status?.toLowerCase();
      if (taskStatus === "hold" || taskStatus === "holding" || taskStatus === "on hold") {
        const confirmWork = window.confirm("This task is currently on hold. Do you want to submit work for this task?");
        if (!confirmWork) {
          // Reset task dropdown value if user cancels
          setFormValues((prevState) => ({
            ...prevState,
            TaskId: "",
          }));
          return;
        }
      }
    }

    setFormValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const isAlreadySubmittedToday = () => {
    if (!formValues.TaskId) return false;

    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDate = today.getDate();

    const allReports = [
      ...(DayReportData?.data || []),
      ...(DayReportData?.pending || []),
    ];

    return allReports.some((report) => {
      const reportTaskId = report?.WorkId?._id || report?.WorkId;
      if (!reportTaskId) return false;
      if (String(reportTaskId) !== String(formValues.TaskId)) return false;

      if (!report?.GeneratedDate) return false;
      const rDate = new Date(report.GeneratedDate);

      // The backend shifts the date by +5.5 hours, so the UTC time of rDate ACTUALLY represents the IST local time.
      const subYear = rDate.getUTCFullYear();
      const subMonth = rDate.getUTCMonth();
      const subDate = rDate.getUTCDate();

      // Check if the submission date matches today's local date
      return (
        subYear === todayYear &&
        subMonth === todayMonth &&
        subDate === todayDate
      );
    });
  };

  const getModalConfig = () => {
    if (isAlreadySubmittedToday()) {
      return {
        title: "Duplicate Report Alert",
        message: "⚠️ You have already submitted a daily report for this task today. Are you sure you want to submit another report for the same task?",
      };
    }
    return {
      title: "Submit Request",
      message: "Are you sure you want to submit this request?",
    };
  };

  const modalConfig = getModalConfig();

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formValues.TaskId || !formValues.Description) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setIsModalOpen(false);
    setIsSubmitting(true);

    const payload = {
      Memberid: formValues.memberid,
      WorkId: formValues.TaskId,
      Description: formValues.Description,
    };

    try {
      await dispatch(EmpDayReport(payload)).unwrap();

      toast.success("Day report submitted successfully!");

      // Reset form
      setFormValues({
        Description: "",
        memberid: parsedData.data._id,
        TaskId: "",
      });
      setRefreshList(prev => prev + 1);
    } catch (err) {
      toast.error("Failed to submit day report.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // const formatDate = (isoDate) => {
  //   const date = new Date(isoDate);
  //   return `${date.getDate().toString().padStart(2, "0")}/${(
  //     date.getMonth() + 1
  //   )
  //     .toString()
  //     .padStart(2, "0")}/${date.getFullYear()}`;
  // };

  const columns = [
    { field: "id", headerName: "No", flex: 1 },
    { field: "GeneratedDate", headerName: "GeneratedDate", flex: 1 },
    { field: "CallNo", headerName: "Call No", flex: 1 },
    { field: "Status", headerName: "Status", flex: 1 },
    {
      field: "Description",
      headerName: "Description",
      width: 300,
      renderCell: (params) => (
        <div style={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
          padding: "2px"
        }}>
          {params.value}
        </div>
      ),
    },
  ];

  const rows =
    DayReportData?.data?.map((el, index) => ({
      id: index + 1,
      MainId: el._id,
      GeneratedDate: formatDisplayDate(el?.GeneratedDate),
      CallNo: el?.WorkId?.CallNo || "--",
      Status: el?.WorkId?.Status || "--",
      Description: el.Description,
    })) || [];



  // 2nd table>>>>>>>>>>>>>>>>>>>>>>>>>

  const Penddingcolumns = [
    { field: "id", headerName: "No", flex: 1 },
    { field: "GeneratedDate", headerName: "GeneratedDate", flex: 1 },
    { field: "CallNo", headerName: "Call No", flex: 1 },
    { field: "Status", headerName: "Status", flex: 1 },
    {
      field: "Description",
      headerName: "Description",
      width: 300,
      renderCell: (params) => (
        <div style={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
          padding: "2px"
        }}>
          {params.value}
        </div>
      ),
    },
  ];
  const Penddingrows =
    DayReportData?.pending?.map((el, index) => ({
      id: index + 1,
      MainId: el._id,
      GeneratedDate: formatDisplayDate(el?.GeneratedDate),
      CallNo: el?.WorkId?.CallNo || "--",
      Status: el?.WorkId?.Status || "--",
      Description: el.Description,
    })) || [];


  if (DayReportData === null || DailyTaskList === null) {
    return <Loader />
  }

  return (
    <div className="p-2 min-h-screen">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
          DAILY WORK SHEET
        </h3>

      </div>

      <div className="Form_bg_color bg-white shadow-md rounded-md mt-4">
        <div className="grid grid-cols-12 gap-6 p-3 rounded-2xl">
          {/* Task Select Dropdown */}
          <div className="col-span-12 lg:col-span-6 md:col-span-6">
            <h5 className="block text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">
              Task Name
            </h5>
            <div className="relative">
              <select
                className="appearance-none w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-[#0c2545] focus:ring-1 focus:ring-[#0c2545] shadow-sm transition-all duration-200 ease-in-out cursor-pointer hover:border-gray-400"
                name="TaskId"
                value={formValues.TaskId}
                onChange={handleChange}
              >
                <option value="">Select a task</option>

                {allTaskOptions.map((opt) => {
                  const isHold = opt.status?.toLowerCase() === 'hold' || opt.status?.toLowerCase() === 'holding' || opt.status?.toLowerCase() === 'on hold';
                  return (
                    <option key={opt.id} value={opt.id}>
                      {opt.callNo} {isHold ? "(Hold)" : `(${opt.status})`}
                    </option>
                  );
                })}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
              </div>
            </div>
          </div>

          {/* Present Status Block */}
          <div className="col-span-12 lg:col-span-6 md:col-span-6">
            <h5 className="block text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">
              Present Status
            </h5>
            <div
              style={{ minHeight: "48px", height: "auto", overflow: "visible" }}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-[13px] text-gray-800 shadow-inner leading-relaxed"
            >
              {selectedTaskDetails ? (selectedTaskDetails.presentStatus || selectedTaskDetails.status || "N/A") : <span className="text-gray-400 italic">Select a task to view present status</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="border rounded shadow-sm p-4 bg-white my-4">
        <div className="mb-4">
          <textarea
            id="Description"
            rows="6"
            name="Description"
            placeholder="DESCRIPTION"
            value={formValues.Description}
            onChange={handleChange}
            className="w-full p-3 text-sm border rounded shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none placeholder:text-gray-400"
          />
        </div>

        <button
          className="bg-[#0c2545] text-white px-4 py-1 rounded text-sm font-semibold hover:bg-[#0a1e39]"
          onClick={handleSubmit}
        >
          SUBMIT
        </button>
      </div>
      <div className="flex justify-end mb-2">
        <span className="bg-[#112A47] text-white px-4 py-2 text-sm font-bold rounded shadow-lg transform hover:scale-105 transition-transform duration-200">
          Total Records: {DayReportData?.totalData || 0}
        </span>
      </div>
      <DynamicTable columns={columns} rows={rows} searchable={false} />

      <TablePagination
        component="div"
        count={DayReportData?.totalData || 0}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        className="bg-white border-t border-gray-200"
      />

      {/* // 2nd table>>>>>>>>>>>>>>>>>>>>>>>>> */}

      <div className="mt-10 mb-5 flex items-center justify-between">
        <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
          Unverified Work
        </h3>
        <span className="bg-[#112A47] text-white px-4 py-2 text-sm font-bold rounded shadow-lg transform hover:scale-105 transition-transform duration-200">
          Pending: {Penddingrows.length}
        </span>
      </div>
      <DynamicTable columns={Penddingcolumns} rows={Penddingrows} searchable={false} />

      <ConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmSubmit}
        title={modalConfig.title}
        message={modalConfig.message}
      />

      {isSubmitting && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] px-3">
          {/* Overlay with Blur */}
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"></div>

          {/* Loader Card */}
          <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.2)] w-full max-w-[420px] overflow-hidden border-[1.5px] border-[#0c2545] flex flex-col items-center pt-8 pb-8 px-6 z-10 transform transition-all animate-in fade-in zoom-in duration-300">
            {/* Spinning Loader Container */}
            <div className="w-[100px] h-[100px] mb-4 flex items-center justify-center relative">
              <Loader />
            </div>

            {/* Text Content */}
            <div className="text-center w-full mt-2">
              <h2 className="text-[#0c2545] text-[22px] font-bold mb-3 tracking-tight">
                Submitting Report
              </h2>
              <p className="text-[#6b7280] text-[15px] leading-relaxed font-medium animate-pulse">
                Submitting daily report, please wait...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyWork;
